import { useState, useRef, DragEvent, ChangeEvent } from 'react'
import { X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import * as XLSX from 'xlsx'

interface FileUploadModalProps {
    isOpen: boolean
    onClose: () => void
    onImport: (file: File) => void
    existingFiles?: string[]
}

export function FileUploadModal({ isOpen, onClose, onImport, existingFiles = [] }: FileUploadModalProps) {
    const [dragActive, setDragActive] = useState(false)
    const [selectedFile, setSelectedFile] = useState<File | null>(null)
    const [error, setError] = useState<string | null>(null)
    const inputRef = useRef<HTMLInputElement>(null)

    if (!isOpen) return null

    const handleDrag = (e: DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    if (e.type === "dragenter" || e.type === "dragover") {
        setDragActive(true)
    } else if (e.type === "dragleave") {
        setDragActive(false)
    }
    }

    const handleDrop = (e: DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setDragActive(false)
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
        const file = e.dataTransfer.files[0]
        if (file.name.toLowerCase().endsWith('.xlsx')) {
            if (existingFiles.includes(file.name)) {
                setError(`A file named "${file.name}" already exists.`)
                setSelectedFile(null)
            } else {
                setSelectedFile(file)
                setError(null)
            }
        } else {
        setError("Invalid file type. Only XLSX is allowed.")
        setSelectedFile(null)
        }
    }
    }

    const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
        const file = e.target.files[0]
        if (file.name.toLowerCase().endsWith('.xlsx')) {
            if (existingFiles.includes(file.name)) {
                setError(`A file named "${file.name}" already exists.`)
                setSelectedFile(null)
            } else {
                setSelectedFile(file)
                setError(null)
            }
        } else {
        setError("Invalid file type. Only XLSX is allowed.")
        setSelectedFile(null)
        }
        e.target.value = ''
    }
    }

    const handleImport = () => {
    if (selectedFile) {
        onImport(selectedFile)
        setSelectedFile(null)
        setError(null)
    }
    }

    const handleClose = () => {
        setSelectedFile(null)
        setError(null)
        onClose()
    }

    const handleDownloadTemplate = () => {
        const headers = [['Question', 'Expected answer', 'Source', 'Page']]
        const ws = XLSX.utils.aoa_to_sheet(headers)
        const wb = XLSX.utils.book_new()
        XLSX.utils.book_append_sheet(wb, ws, "Template")
        XLSX.writeFile(wb, "sample_template.xlsx")
    }

    return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm animate-in fade-in duration-200">
        <div className="bg-white rounded-xl shadow-xl w-[600px] max-w-[90%] p-6 animate-in fade-in zoom-in-95 duration-200">
        <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-bold text-gray-900">Import XLSX</h2>
            <button onClick={handleClose}><X className="text-gray-400 hover:text-gray-600" /></button>
        </div>

        <p className="text-gray-600 mb-4 text-[15px]">Download the <span onClick={handleDownloadTemplate} className="text-[#D91F27] underline cursor-pointer">sample template</span> and upload your XLSX file.</p>

        <div
            className={`border-2 border-dashed rounded-lg p-10 flex flex-col items-center justify-center mb-6 transition-colors ${
            dragActive ? "border-[#D91F27] bg-red-50" : "border-gray-300"
            }`}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
        >
            {selectedFile ? (
            <div className="text-center">
                <p className="font-medium text-gray-900">{selectedFile.name}</p>
                <button onClick={() => setSelectedFile(null)} className="text-[#D91F27] text-sm mt-2 hover:underline">Remove</button>
            </div>
            ) : (
            <>
                <p className="text-gray-900 font-medium mb-2">Drag your file here</p>
                <p className="text-gray-500 text-sm mb-4">or</p>
                <Button variant="outline" onClick={() => inputRef.current?.click()}>Browse</Button>
                <input
                    ref={inputRef}
                    type="file"
                    accept=".xlsx"
                    className="hidden"
                    onChange={handleChange}
                />
            </>
            )}
        </div>
        {error && <p className="text-[#D91F27] text-sm mb-6 text-center">{error}</p>}

        <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={handleClose} className="border-gray-300 text-gray-700 font-medium px-4">Cancel</Button>
            <Button className="bg-[#D91F27] hover:bg-[#b81a21] text-white font-medium px-4" disabled={!selectedFile} onClick={handleImport}>Import</Button>
        </div>
        </div>
    </div>
    )
}
